import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import useAuthStore from '../../lib/auth-store';
import { attrFetch, roleLabel } from './attributionApi';
import { PartyPicker } from './AttributionDialogs';
import { DateField } from './DateField';

const SERVICING = ['servicing_agent', 'account_manager', 'support_agent', 'installation_agent'];

function Preview({ plan }) {
  if (!plan) return null;
  const rows = [...plan.relationships.map((r) => ({ ...r, what: roleLabel(r.role) })),
    ...plan.attributions.map((a) => ({ ...a, what: `attribution (${roleLabel(a.scope)})` }))];
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 max-h-48 overflow-auto" data-testid="book-transfer-preview">
      <p className="text-xs font-medium px-3 py-2 bg-gray-50 dark:bg-gray-800 sticky top-0" data-testid="book-transfer-preview-count">
        {rows.length} assignment{rows.length !== 1 ? 's' : ''} across {plan.merchant_count} merchant{plan.merchant_count !== 1 ? 's' : ''}
      </p>
      {rows.map((r) => (
        <div key={r.relationship_id || r.attribution_id} className="flex justify-between px-3 py-1.5 text-xs border-t border-gray-100 dark:border-gray-800">
          <span className="truncate">{r.merchant_name || r.merchant_group_id}</span>
          <span className="text-gray-500 capitalize">{r.what}</span>
        </div>
      ))}
    </div>
  );
}

export default function BookTransferDialog({ open, onClose, onDone }) {
  const { token } = useAuthStore();
  const [parties, setParties] = useState([]);
  const [f, setF] = useState({ from: '', to: '', date: '', reason: '', roles: SERVICING, attribution: false });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) attrFetch(token, '/relationship-parties').then(setParties).catch((e) => toast.error(e.message)); }, [open, token]);
  useEffect(() => setPlan(null), [f.from, f.to, f.roles, f.attribution]);

  const body = (dry) => ({ from_user_id: f.from, to_user_id: f.to, reason: f.reason, roles: f.roles,
    include_attribution: f.attribution, dry_run: dry, effective_from: f.date ? new Date(f.date).toISOString() : undefined });
  const go = async (dry) => {
    setBusy(true);
    try {
      const res = await attrFetch(token, '/book-transfer', 'POST', body(dry));
      if (dry) { setPlan(res); return; }
      toast.success(`Moved ${res.moved_relationships} relationship(s)${res.transferred_attributions ? ` and ${res.transferred_attributions} attribution(s)` : ''}`);
      if (res.skipped?.length) toast.warning(`${res.skipped.length} item(s) skipped`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const toggleRole = (r) => setF({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] });
  const ready = f.from && f.to && f.reason.trim() && f.roles.length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="book-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" /> Bulk book transfer</DialogTitle>
          <DialogDescription>Move a departing agent's servicing book in one dated step. Previous holders stay in history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-xs mb-1 text-gray-500">From (departing)</p><PartyPicker parties={parties} value={f.from} onChange={(v) => setF({ ...f, from: v })} testId="book-transfer-from" /></div>
            <div><p className="text-xs mb-1 text-gray-500">To</p><PartyPicker parties={parties.filter((p) => p.id !== f.from)} value={f.to} onChange={(v) => setF({ ...f, to: v })} testId="book-transfer-to" /></div>
          </div>
          <div className="flex flex-wrap gap-3">
            {SERVICING.map((r) => (
              <label key={r} className="flex items-center gap-1.5 text-xs capitalize">
                <Checkbox checked={f.roles.includes(r)} onCheckedChange={() => toggleRole(r)} data-testid={`book-transfer-role-${r}`} /> {roleLabel(r)}
              </label>
            ))}
          </div>
          <label className="flex items-start gap-2 text-xs rounded-md bg-amber-50 dark:bg-amber-900/20 p-2">
            <Checkbox checked={f.attribution} onCheckedChange={(v) => setF({ ...f, attribution: !!v })} data-testid="book-transfer-include-attribution" />
            <span>Also transfer origination attribution. The original record is closed at the effective date, never rewritten.</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-xs mb-1 text-gray-500">Effective from (defaults to now)</p><DateField value={f.date} onChange={(v) => setF({ ...f, date: v })} testId="book-transfer-date" /></div>
            <div><p className="text-xs mb-1 text-gray-500">Reason (required)</p><Input placeholder="e.g. Agent left the company" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="book-transfer-reason" /></div>
          </div>
          <Preview plan={plan} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => go(true)} disabled={!ready || busy} data-testid="book-transfer-preview-btn">Preview</Button>
          <Button onClick={() => go(false)} disabled={!ready || busy || !plan} data-testid="book-transfer-confirm-btn">{busy ? 'Working…' : 'Transfer book'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
